/**
 * RolesWithPermissions - Combined Role Management with Permissions
 * 
 * This component combines:
 * 1. Role CRUD operations (create, edit, delete roles)
 * 2. Permission assignment for each role
 * 
 * New roles automatically get READ permissions for all sections by default.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Collapse,
  IconButton,
  Checkbox,
  useTheme,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import { Shield, Plus, ChevronDown, Loader2, Trash2, Edit, Users, Lock } from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';

import ResponsiveTable from '../Components/UI/ResponsiveTable';
import FullPageLoader from '../Components/UI/FullPageLoader';
import { ActionMenu } from '../Components/UI/ResponsiveTable';
import { usePermissions } from '../contexts/PermissionContext';
import { useOrg } from '../contexts/OrgContext';
import BASE_URL from '../config';

// Helper to initialize sections with default READ permission
const initializeSectionPermissions = (sections, setReadByDefault = false) => {
  return sections.map((section, index) => ({
    ...section,
    number: `${index + 1}.`,
    permissions: section.permissions.map(p => ({
      ...p,
      create: false,
      update: false,
      read: setReadByDefault, // Set read to true for new roles
      delete: false,
      access: setReadByDefault && section.hasAccess, // For access-only sections
    }))
  }));
};

function RolesWithPermissions() {
  const theme = useTheme();
  const { can } = usePermissions();
  const { selectedOrgId } = useOrg();
  
  // Roles state
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Role form state
  const [roleForm, setRoleForm] = useState({
    role_name: '',
    description: '',
  });
  
  // Permission sections state
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const canCreate = can('Admin', 'roles', 'create');
  const canEdit = can('Admin', 'roles', 'update');
  const canDelete = can('Admin', 'roles', 'delete');
  const canView = can('Admin', 'roles', 'read');

  // Fetch roles with permission counts
  const fetchRoles = useCallback(async () => {
    if (!selectedOrgId) return;
    setLoading(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      const response = await axios.get(
        `${BASE_URL}/api/designations?organisation_id=${selectedOrgId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Get permission counts for each role
      const rolesWithCounts = await Promise.all(
        response.data.map(async (role) => {
          try {
            const permRes = await axios.get(
              `${BASE_URL}/api/user-permissions/${role.role_code}?organisation_id=${selectedOrgId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const permCount = permRes.data?.filter(p => 
              p.can_create || p.can_update || p.can_read || p.can_delete || p.has_access
            ).length || 0;
            return { ...role, id: role.role_code, permission_count: permCount };
          } catch {
            return { ...role, id: role.role_code, permission_count: 0 };
          }
        })
      );

      setRoles(rolesWithCounts);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId]);

  // Fetch permission sections from API
  const fetchPermissionSections = async () => {
    try {
      setLoadingSections(true);
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      const response = await axios.get(`${BASE_URL}/api/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.sections) {
        return response.data.sections;
      }
      return [];
    } catch (error) {
      console.error('Error fetching permission sections:', error);
      toast.error('Failed to load permission sections');
      return [];
    } finally {
      setLoadingSections(false);
    }
  };

  // Fetch existing permissions for a role
  const fetchRolePermissions = async (roleCode) => {
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      const res = await axios.get(
        `${BASE_URL}/api/user-permissions/${roleCode}?organisation_id=${selectedOrgId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return res.data || [];
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      return [];
    }
  };

  useEffect(() => {
    if (selectedOrgId) {
      fetchRoles();
    }
  }, [selectedOrgId, fetchRoles]);

  // Open modal for creating new role
  const handleCreate = async () => {
    setEditingRole(null);
    setRoleForm({ role_name: '', description: '' });
    
    // Fetch sections and initialize with READ permissions by default
    const rawSections = await fetchPermissionSections();
    const initializedSections = initializeSectionPermissions(rawSections, true); // true = set read by default
    setSections(initializedSections);
    setCollapsedSections({});
    setModalOpen(true);
  };

  // Open modal for editing existing role
  const handleEdit = async (role) => {
    setEditingRole(role);
    setRoleForm({
      role_name: role.role_name || '',
      description: role.description || '',
    });

    // Fetch sections
    const rawSections = await fetchPermissionSections();
    
    // Fetch existing permissions for this role
    const rolePermissions = await fetchRolePermissions(role.role_code);

    // Initialize sections with existing permissions
    const initializedSections = rawSections.map((section, index) => ({
      ...section,
      number: `${index + 1}.`,
      permissions: section.permissions.map(p => {
        const match = rolePermissions.find(
          rp => rp.section === section.id && rp.permission_key === p.id
        );
        return match
          ? {
              ...p,
              create: match.can_create || false,
              update: match.can_update || false,
              read: match.can_read || false,
              delete: match.can_delete || false,
              access: match.has_access || false,
            }
          : { ...p, create: false, update: false, read: false, delete: false, access: false };
      }),
    }));

    setSections(initializedSections);
    setCollapsedSections({});
    setModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteClick = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;
    
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      await axios.delete(
        `${BASE_URL}/api/designations/${roleToDelete.role_code}?organisation_id=${selectedOrgId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.error || 'Failed to delete role');
    } finally {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  // Toggle section collapse
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Handle permission checkbox change
  const CRUD_TYPES = ["create", "update", "delete"];

  const handlePermissionChange = (sectionId, permissionId, type, checked) => {
    setSections(prev =>
      prev.map(section =>
        section.id === sectionId
          ? {
              ...section,
              permissions: section.permissions.map(permission => {
                if (permission.id !== permissionId) return permission;

                let updated = { ...permission, [type]: checked };

                const hasAnyCrud = updated.create || updated.update || updated.delete;

                if (CRUD_TYPES.includes(type)) {
                  if (checked) {
                    updated.read = true;
                  } else {
                    updated.read = hasAnyCrud ? true : false;
                  }
                }

                if (type === "read" && !checked) {
                  updated.create = false;
                  updated.update = false;
                  updated.delete = false;
                }

                return updated;
              }),
            }
          : section
      )
    );
  };

  // Handle select all for a section column
  const handleSelectAllSection = (sectionId, type) => {
    setSections(prev =>
      prev.map(section => {
        if (section.id !== sectionId) return section;

        const allSelected = section.permissions.every(p => p[type]);

        return {
          ...section,
          permissions: section.permissions.map(permission => {
            let updated = { ...permission, [type]: !allSelected };

            if (CRUD_TYPES.includes(type)) {
              if (updated[type]) {
                updated.read = true;
              } else {
                const stillCrud = CRUD_TYPES.some(t => t !== type && updated[t]);
                updated.read = stillCrud ? true : false;
              }
            }

            if (type === "read" && !updated.read) {
              updated.create = false;
              updated.update = false;
              updated.delete = false;
            }

            return updated;
          }),
        };
      })
    );
  };

  // Save role and permissions
  const handleSave = async () => {
    if (!roleForm.role_name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setSaving(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      let roleCode;

      if (editingRole) {
        // Update existing role
        await axios.put(
          `${BASE_URL}/api/designations/${editingRole.role_code}`,
          { ...roleForm, organisation_id: selectedOrgId },
          { headers }
        );
        roleCode = editingRole.role_code;
        
        // Update permissions
        await axios.put(
          `${BASE_URL}/api/user-permissions/${roleCode}?organisation_id=${selectedOrgId}`,
          { designation_code: roleCode, sections },
          { headers }
        );

        toast.success('Role updated successfully');
      } else {
        // Create new role
        const roleResponse = await axios.post(
          `${BASE_URL}/api/designations`,
          { ...roleForm, organisation_id: selectedOrgId },
          { headers }
        );
        roleCode = roleResponse.data.role_code;

        // Create permissions for new role (already have READ by default in sections)
        await axios.post(
          `${BASE_URL}/api/user-permissions?organisation_id=${selectedOrgId}`,
          { designation_code: roleCode, sections },
          { headers }
        );

        toast.success('Role created successfully with default READ permissions');
      }

      setModalOpen(false);
      fetchRoles();
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error(error.response?.data?.error || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  // Export roles
  const handleExport = () => {
    const csvContent = [
      ['Role Code', 'Role Name', 'Description', 'Permissions Count'],
      ...filteredRoles.map(role => [
        role.role_code || '',
        role.role_name || '',
        role.description || '',
        role.permission_count || '0'
      ])
    ]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `roles_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredRoles = roles.filter(role => {
    const search = searchTerm.toLowerCase();
    return (
      (role.role_name || '').toLowerCase().includes(search) ||
      (role.role_code || '').toLowerCase().includes(search) ||
      (role.description || '').toLowerCase().includes(search)
    );
  });

  const columns = [
    {
      field: 'role_code',
      headerName: 'Role Code',
      sortable: true,
      renderCell: ({ value }) => (
        <Chip
          label={value || 'N/A'}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      )
    },
    {
      field: 'role_name',
      headerName: 'Role Name',
      sortable: true,
      renderCell: ({ value, row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Shield size={16} />
          <span style={{ fontWeight: 500 }}>{value}</span>
          {row.is_system_role && (
            <Tooltip title="System role — cannot be modified">
              <Chip icon={<Lock size={12} />} label="System" size="small" variant="outlined" />
            </Tooltip>
          )}
        </Box>
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      sortable: false,
      renderCell: ({ value }) => (
        <span style={{ color: 'var(--neutral-600)' }}>
          {value || 'No description'}
        </span>
      )
    },
    {
      field: 'permission_count',
      headerName: 'Permissions',
      sortable: true,
      renderCell: ({ value }) => (
        <Chip
          label={`${value || 0} configured`}
          size="small"
          color={value > 0 ? "success" : "default"}
          variant="filled"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      align: 'right',
      renderCell: ({ row }) => (
        <ActionMenu
          rowData={row}
          onEdit={canEdit && !row.is_system_role ? () => handleEdit(row) : null}
          onDelete={canDelete && !row.is_system_role ? () => handleDeleteClick(row) : null}
        />
      )
    }
  ];

  if (!canView) {
    return null;
  }

  if (loading && roles.length === 0) {
    return <FullPageLoader message="Loading roles..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <ResponsiveTable
        data={filteredRoles}
        columns={columns}
        title="Role & Permission Management"
        subtitle={`${filteredRoles.length} role(s) configured`}
        onSearch={setSearchTerm}
        onExport={handleExport}
        onRefresh={fetchRoles}
        actions={
          canCreate && (
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={handleCreate}
            >
              Add Role
            </Button>
          )
        }
        emptyMessage="No roles configured. Create a new role to get started."
      />

      {/* Role & Permission Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: theme.palette.primary.main,
            color: 'white',
            py: 2.5,
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Shield size={24} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editingRole ? 'Edit Role & Permissions' : 'Create New Role'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={() => setModalOpen(false)}
              variant="text"
              disabled={saving}
              sx={{ color: 'white', textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={saving || !!editingRole?.is_system_role}
              onClick={handleSave}
              color="inherit"
              sx={{
                backgroundColor: 'white',
                color: theme.palette.primary.main,
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              {saving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving...
                </Box>
              ) : (
                editingRole ? 'Update Role' : 'Create Role'
              )}
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, backgroundColor: theme.palette.background.default }}>
          {/* Role Details Section */}
          <Box sx={{ p: 3, backgroundColor: 'white', borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Role Details
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Role Name"
                value={roleForm.role_name}
                onChange={(e) => setRoleForm({ ...roleForm, role_name: e.target.value })}
                required
                fullWidth
                size="small"
                disabled={!!editingRole?.is_system_role}
                helperText={editingRole?.is_system_role ? 'System role — cannot be renamed' : undefined}
              />
              <TextField
                label="Description"
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                fullWidth
                size="small"
              />
            </Box>
            {!editingRole && (
              <Alert severity="info" sx={{ mt: 2 }}>
                New roles are created with <strong>READ</strong> permissions by default for all sections.
              </Alert>
            )}
          </Box>

          {/* Permissions Section */}
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Permissions
            </Typography>

            {loadingSections ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading permission sections...</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sections.map((section) => {
                  const isCollapsed = collapsedSections[section.id];

                  return (
                    <Paper
                      key={section.id}
                      elevation={0}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        backgroundColor: 'white',
                      }}
                    >
                      {/* Section Header */}
                      <Box
                        onClick={() => toggleSection(section.id)}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: section.hasAccess
                            ? '1fr 100px 40px'
                            : '1fr 85px 85px 85px 85px 40px',
                          alignItems: 'center',
                          gap: 1,
                          p: 2,
                          background: theme.palette.background.default,
                          cursor: 'pointer',
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Shield size={18} color={theme.palette.primary.main} />
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                            {section.number} {section.title}
                          </Typography>
                        </Box>

                        {section.hasAccess ? (
                          <Chip
                            label="ACCESS"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectAllSection(section.id, "access");
                            }}
                            sx={{
                              height: '28px',
                              backgroundColor: theme.palette.primary.main,
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.6875rem',
                              cursor: 'pointer',
                            }}
                          />
                        ) : (
                          <>
                            {["create", "update", "read", "delete"].map(type => (
                              <Chip
                                key={type}
                                label={type.toUpperCase()}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectAllSection(section.id, type);
                                }}
                                sx={{
                                  height: '28px',
                                  backgroundColor: theme.palette.primary.main,
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '0.6875rem',
                                  cursor: 'pointer',
                                }}
                              />
                            ))}
                          </>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <ChevronDown
                            size={20}
                            style={{
                              transform: isCollapsed ? '' : 'rotate(180deg)',
                              transition: 'transform 0.3s',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Permission Rows */}
                      <Collapse in={!isCollapsed} timeout={300}>
                        <Box sx={{ backgroundColor: 'white' }}>
                          {section.permissions.map((permission, index) => (
                            <Box
                              key={permission.id}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: section.hasAccess
                                  ? '1fr 100px 40px'
                                  : '1fr 85px 85px 85px 85px 40px',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1.5,
                                borderTop: index === 0 ? 'none' : `1px solid ${theme.palette.divider}`,
                                '&:hover': { backgroundColor: theme.palette.action.hover },
                              }}
                            >
                              <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary, fontWeight: 600 }}>
                                {permission.name}
                              </Typography>

                              {section.hasAccess ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Checkbox
                                    checked={permission.access || false}
                                    disabled={!!editingRole?.is_system_role}
                                    onChange={(e) => handlePermissionChange(section.id, permission.id, "access", e.target.checked)}
                                    sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }}
                                  />
                                </Box>
                              ) : (
                                <>
                                  {["create", "update", "read", "delete"].map(type => {
                                    const hasCrud = permission.create || permission.update || permission.delete;
                                    const isReadDisabled = (type === "read" && hasCrud) || !!editingRole?.is_system_role;

                                    return (
                                      <Box key={type} sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Checkbox
                                          checked={permission[type] || false}
                                          disabled={isReadDisabled || (type !== "read" && !!editingRole?.is_system_role)}
                                          onChange={(e) => handlePermissionChange(section.id, permission.id, type, e.target.checked)}
                                          sx={{
                                            '&.Mui-checked': { color: theme.palette.primary.main },
                                            '&.Mui-disabled': { opacity: 0.5 },
                                          }}
                                        />
                                      </Box>
                                    );
                                  })}
                                </>
                              )}

                              <Box />
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role <strong>{roleToDelete?.role_name}</strong>?
            This will also remove all associated permissions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            startIcon={<Trash2 size={16} />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RolesWithPermissions;
